import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { FileSpreadsheet } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { getCatalogoReportes, type ValoresFiltro } from '@/services/api/reportes.api';
import { ReportDialog } from './ReportDialog';

interface ReportButtonProps {
  /** Clave del reporte, p. ej. `inventario-activos`. */
  reporte: string;
  /** Filtros de la pantalla, para abrir el diálogo con lo que ya se ve. */
  filtros?: ValoresFiltro;
  /** Texto del botón. Por defecto, "Reporte". */
  label?: string;
  variant?: 'default' | 'outline' | 'ghost';
  className?: string;
}

/**
 * Botón de reporte reutilizable.
 *
 * Se monta igual en cualquier módulo — `<ReportButton reporte="…" />` — y se
 * oculta solo si el rol de la sesión no tiene ese reporte en su catálogo, así
 * que las pantallas no necesitan repetir la lógica de permisos.
 *
 * El catálogo se consulta una sola vez y queda en caché para toda la sesión.
 */
export function ReportButton({
  reporte,
  filtros,
  label = 'Reporte',
  variant = 'outline',
  className,
}: ReportButtonProps) {
  const [abierto, setAbierto] = useState(false);

  const { data: catalogo } = useQuery({
    queryKey: ['reportes-catalogo'],
    queryFn: getCatalogoReportes,
    staleTime: Infinity,
  });

  // Mientras carga el catálogo no se pinta nada: mostrar el botón y luego
  // esconderlo se ve peor que aparecer una vez.
  if (!catalogo?.some((r) => r.clave === reporte)) return null;

  return (
    <>
      <Button variant={variant} onClick={() => setAbierto(true)} className={className}>
        <FileSpreadsheet className="h-4 w-4" />
        {label}
      </Button>

      {/* Montado solo al abrir: así el esquema y la vista previa no se
          consultan en pantallas donde nadie toca el botón. */}
      {abierto && (
        <ReportDialog
          reporte={reporte}
          filtrosIniciales={filtros}
          open={abierto}
          onOpenChange={setAbierto}
        />
      )}
    </>
  );
}
